/**
 * @fileoverview Helper para generar archivos Excel de Solicitudes de Materiales
 */

import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { formatProductoNombreForExcel } from '../utils/productoInventarioHelper';

/**
 * Agrupa solicitudes por nombre de producto_inventario
 * @param {Array} solicitudes - Array de solicitudes con expand
 * @returns {Object} Objeto con claves siendo nombres de productos_inventario y valores siendo arrays de solicitudes
 */
export function agruparPorMaterial(solicitudes) {
  const agrupadas = {};

  solicitudes.forEach(solicitud => {
    const productoInv = solicitud.expand?.producto_inventario;
    // Usar formatProductoNombreForExcel para incluir medida concatenada
    const nombreProducto = formatProductoNombreForExcel(productoInv);

    if (!agrupadas[nombreProducto]) {
      agrupadas[nombreProducto] = [];
    }
    agrupadas[nombreProducto].push(solicitud);
  });

  return agrupadas;
}

/**
 * Calcula la cantidad solicitada (valor positivo del disponible)
 * @param {Object} solicitud - Solicitud con expand de producto_inventario
 * @returns {number} Cantidad solicitada (valor positivo)
 */
export function calcularCantidadSolicitada(solicitud) {
  const inv = solicitud.expand?.producto_inventario;
  
  if (!inv) {
    // Fallback al total de la solicitud cuando no hay producto_inventario expandido
    return solicitud.total || 0;
  }

  const total = inv.total || 0;
  const reservado = inv.reservado || 0;
  const solicitado = inv.solicitado || 0;
  const disponible = total - reservado - solicitado;

  // Retornar valor absoluto del disponible (siempre positivo)
  return Math.abs(disponible);
}

/**
 * Genera un archivo Excel con las solicitudes de materiales agrupadas por producto_inventario
 * @param {Array} solicitudes - Array de solicitudes (con expand de orden_produccion, producto_inventario, tinta)
 * @returns {void}
 */
export function generateExcel(solicitudes) {
  try {
    if (!solicitudes || solicitudes.length === 0) {
      throw new Error('No hay solicitudes para exportar');
    }

    // Agrupar solicitudes por producto_inventario
    const agrupadas = agruparPorMaterial(solicitudes);

    // Crear libro de trabajo
    const wb = XLSX.utils.book_new();

    // Headers
    const headers = ['Número Orden', 'Cliente', 'Producto', 'Producto Inventario', 'Cantidad Solicitada', 'Fecha Solicitud'];

    // Función helper para transformar solicitud a fila
    const transformSolicitudToRow = (solicitud) => {
      const orden = solicitud.expand?.orden_produccion;
      const productoInv = solicitud.expand?.producto_inventario;
      const cantidadSolicitada = calcularCantidadSolicitada(solicitud);
      const fechaSolicitud = solicitud.created 
        ? new Date(solicitud.created).toLocaleDateString() 
        : '';

      // Obtener nombre del producto_inventario para la columna (con medida concatenada)
      const nombreProductoColumna = formatProductoNombreForExcel(productoInv);
      
      // Obtener cliente y producto de la orden
      const clienteNombre = orden?.expand?.cliente?.nombre || orden?.expand?.cliente?.razon_social || 'N/A';
      const productoNombre = orden?.expand?.producto?.nombre || 'N/A';

      return [
        orden?.numero || 'N/A',
        clienteNombre,
        productoNombre,
        nombreProductoColumna,
        cantidadSolicitada,
        fechaSolicitud
      ];
    };

    // Crear primera hoja con TODOS los materiales
    const allRows = solicitudes.map(transformSolicitudToRow);
    
    // Calcular total general
    const totalGeneral = solicitudes.reduce((sum, solicitud) => {
      return sum + calcularCantidadSolicitada(solicitud);
    }, 0);
    
    // Agregar fila de total general
    allRows.push(['', '', '', 'Total General', totalGeneral, '']);

    // Crear hoja de trabajo para todos los materiales
    const wsAll = XLSX.utils.aoa_to_sheet([headers, ...allRows]);
    applyStyles(wsAll, allRows.length + 1); // +1 para incluir header
    XLSX.utils.book_append_sheet(wb, wsAll, 'Todos los Materiales');

    // Crear una hoja por cada producto_inventario (pestañas individuales)
    Object.keys(agrupadas).forEach(nombreProducto => {
      const solicitudesProducto = agrupadas[nombreProducto];

      // Transformar datos al formato requerido
      const rows = solicitudesProducto.map(transformSolicitudToRow);

      // Calcular total
      const totalCantidad = solicitudesProducto.reduce((sum, solicitud) => {
        return sum + calcularCantidadSolicitada(solicitud);
      }, 0);

      // Agregar fila de total
      rows.push(['', '', '', 'Total', totalCantidad, '']);

      // Crear hoja de trabajo
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

      // Aplicar estilos
      applyStyles(ws, rows.length + 1); // +1 para incluir header

      // Agregar hoja al libro (limitar nombre de pestaña a 31 caracteres - límite de Excel)
      const sheetName = nombreProducto.length > 31 ? nombreProducto.substring(0, 31) : nombreProducto;
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    // Generar archivo y descargar
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const fileName = `Solicitudes_Inventario_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(blob, fileName);
  } catch (error) {
    console.error('Error generating Excel:', error);
    throw error;
  }
}

/**
 * Aplica estilos a la hoja de trabajo
 * @param {Object} ws - Hoja de trabajo
 * @param {number} totalRows - Número total de filas (incluyendo header)
 */
function applyStyles(ws, totalRows) {
  if (!ws || !ws['!ref']) {
    return;
  }

  const range = XLSX.utils.decode_range(ws['!ref']);

  // Estilos de headers
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
    if (!ws[cellRef]) continue;

    ws[cellRef].s = {
      font: { bold: true },
      fill: { fgColor: { rgb: 'D3D3D3' } },
      alignment: { wrapText: true, vertical: 'center' }
    };
  }

  // Estilos para todas las celdas de datos
  for (let row = range.s.r; row <= range.e.r; row++) {
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
      if (!ws[cellRef]) continue;

      // Si no tiene estilo, aplicar estilo por defecto
      if (!ws[cellRef].s) {
        ws[cellRef].s = {
          alignment: { wrapText: true, vertical: 'top' }
        };
      }

      // Fila de total (última fila) - fondo amarillo en columna de cantidad
      if (row === range.e.r && col === 4) { // Columna E (índice 4) - Cantidad Solicitada
        ws[cellRef].s.fill = { fgColor: { rgb: 'FFFF00' } };
        ws[cellRef].s.font = { bold: true };
      }
    }
  }

  // Ajustar anchos de columna
  const colWidths = [
    { wch: 15 }, // Número Orden
    { wch: 25 }, // Cliente
    { wch: 25 }, // Producto
    { wch: 30 }, // Producto Inventario
    { wch: 20 }, // Cantidad Solicitada
    { wch: 18 }  // Fecha Solicitud
  ];
  ws['!cols'] = colWidths;

  // Ajustar altura de filas
  ws['!rows'] = [];
  for (let row = 0; row <= range.e.r; row++) {
    ws['!rows'][row] = { hpt: 20 };
  }
}

